import { sha256 } from "js-sha256";
import _round from "lodash/round";
import { send } from "./GoogleAnalyticsClient";
import { GaProductsState, getGaProducts, getGaRevenue, getGaSalesType } from "./state/selectors";
import { GA_TEXT_CONSTS as TEXT_CONSTS, REALM, GTM_ECOMMERCE_STEP } from "../../../constants";
import {
    GAAddToCartEvent,
    GAActionField,
    GACheckoutEvent,
    GARemoveFromCartEvent,
    GAProduct,
    GATransactionEvent,
    UIEvent,
} from "../../../models";
import { getOrderId, getRealm } from "../app/state/selectors";
import { getInitiatedBy } from "../checkout/state/selectors/accountSelectors";
import { getLegalOwnerEmail, getLegalOwnerPhone } from "../checkout/state/selectors/customerInfoSelectors";
import { getDeliveryInfoSelectedPrice, getDeliveryInfoType } from "../checkout/state/selectors/deliveryInfoSelectors";

const createCheckoutGaObject: (actionField: GAActionField, products: GAProduct[]) => GACheckoutEvent = (
    actionField,
    products,
) => ({
    event: TEXT_CONSTS.EVENTS.CHECKOUT,
    ecommerce: {
        checkout: {
            actionField,
            products,
        },
    },
});

const createTransactionGaObject: (
    actionField: GAActionField,
    products: GAProduct[],
    hashedLegalEmail: string,
    hashedLegalPhone: string,
) => GATransactionEvent = (
    actionField: GAActionField,
    products: GAProduct[],
    hashedLegalEmail: string,
    hashedLegalPhone: string,
) => ({
    event: TEXT_CONSTS.EVENTS.TRANSACTION,
    ecommerce: {
        purchase: {
            actionField,
            products,
        },
    },
    em: hashedLegalEmail,
    ph: hashedLegalPhone,
});

export const getAffiliation: (state: GaProductsState) => string = (state) => {
    let affiliation;
    const realm = getRealm(state);
    const initiatedBy = getInitiatedBy(state);
    if (initiatedBy) {
        if (initiatedBy === `app`) affiliation = TEXT_CONSTS.AFFILIATION_APP;
        else if (initiatedBy === `minside`) affiliation = TEXT_CONSTS.AFFILIATION_MINSIDE;
    } else if (REALM.B2B === realm) {
        affiliation = TEXT_CONSTS.AFFILIATION_B2B;
    } else affiliation = TEXT_CONSTS.AFFILIATION;

    return affiliation || TEXT_CONSTS.AFFILIATION;
};

// Add "Personlige Opplysninger", "Eier", "Medlem" and "Levering" to funnel
export const trackCheckout: (step: number, state: GaProductsState) => Promise<unknown> = (step, state) => {
    const products = getGaProducts(state);
    let actionField: GAActionField = {
        step,
        action: "checkout",
    };
    if (step === GTM_ECOMMERCE_STEP.PERSONAL_DETAILS) {
        actionField = {
            ...actionField,
            "Personlige opplysninger": null,
            option: getGaSalesType(state, ""),
        };
    } else if (step === GTM_ECOMMERCE_STEP.LEGAL) {
        actionField = {
            ...actionField,
            Eier: null,
            option: getGaSalesType(state, ""),
        };
    } else if (step === GTM_ECOMMERCE_STEP.MEMBER) {
        actionField = {
            ...actionField,
            Medlem: null,
            option: getDeliveryInfoType(state),
        };
    } else if (step === GTM_ECOMMERCE_STEP.SUMMARY) {
        actionField = {
            ...actionField,
            Levering: null,
            option: getDeliveryInfoType(state),
            shippingPrice: getDeliveryInfoSelectedPrice(state),
        };
    }
    return send(createCheckoutGaObject(actionField, products), true);
};

export const getHashedLegalEmail = (state: GaProductsState) => {
    const email = getLegalOwnerEmail(state).toLocaleLowerCase().trim();
    return sha256(email);
};

export const getHashedLegalPhone = (state: GaProductsState) => {
    const phone = getLegalOwnerPhone(state);
    const phoneWithCountryCode = `47${phone}`;
    return sha256(phoneWithCountryCode);
};

export const trackTransaction: (state: GaProductsState) => Promise<unknown> = (state) => {
    const products = getGaProducts(state);
    const revenue = getGaRevenue(products);
    const actionField: GAActionField = {
        id: getOrderId(state),
        affiliation: getAffiliation(state),
        revenue,
        tax: _round(revenue - revenue / 1.25, 2),
        shipping: getDeliveryInfoSelectedPrice(state),
    };
    const hashedLegalEmail = getHashedLegalEmail(state);
    const hashedLegalPhone = getHashedLegalPhone(state);
    const transactionData = createTransactionGaObject(actionField, products, hashedLegalEmail, hashedLegalPhone);

    return send(transactionData);
};

export const trackProducts: (state: GaProductsState, action: "add" | "remove") => Promise<unknown> = (
    state,
    action,
) => {
    const products = getGaProducts(state);

    if (action === "add") {
        const addToCartData: GAAddToCartEvent = {
            event: "addToCart",
            ecommerce: {
                add: { products },
            },
        };
        return send(addToCartData);
    }

    const removeFromCartData: GARemoveFromCartEvent = {
        event: "removeFromCart",
        ecommerce: {
            remove: { products },
        },
    };
    return send(removeFromCartData);
};

export const trackUIClickEvent: (uie: UIEvent) => Promise<unknown> = (uie: UIEvent) =>
    send({
        event: TEXT_CONSTS.EVENTS.UI_CLICK,
        ui_item_action: uie.action,
        ui_item_context: uie.context,
        ui_item_text: uie.text,
        ui_item_type: uie.type,
        ui_item_url: uie.url,
    });

export const trackWebshopClickEvent: (uie: UIEvent) => Promise<unknown> = (uie: UIEvent) =>
    send({
        event: TEXT_CONSTS.EVENTS.WEBSHOP_CLICK,
        ui_item_action: uie.action,
        ui_item_context: uie.context,
        ui_item_text: uie.text,
        ui_item_type: uie.type,
        ui_item_url: uie.url,
    });
